import * as React from 'react'
import { UserDto } from '../../dtos/user.dto'
import { validateSession } from '../../_utils/session-helpers'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../stores/app.state'
import { AppService } from '../../services/index'
import { Dispatch } from 'react'
import {
    AUTH0_TENANT_ID,
    AUTH0_CLIENT_ID,
    AUTH0_PLATFORM_REDIRECT_PATHNAME,
    AUTH0_STATE_SESSION_KEY,
} from '../../constants'
import axios from 'axios'
import aqe from '@pushly/aqe'
import { useLocation } from 'react-router-dom'
import { parseQueryStringParams } from '../../_utils/query-string'
import { ORGANIZATIONS_ROUTE } from '@halo/portals/post-auth/accounts'
import { LoadingScreen } from '@halo/components/loading-screen/loading-screen'
import { SessionValidationFailedException } from '@halo/exceptions/session-validation.exception'
import { Auth0InvalidAuthorizationParamsException } from '@halo/exceptions/auth0-invalid-authorization-params.exception'
import { ErrorPage } from '@halo/components/error-page/error-page'

export type PlatformUser =
    | {
          authenticated: false
          revalidate?: boolean
      }
    | {
          authenticated: true
          user: UserDto
          revalidate?: boolean
      }

type UserContext = PlatformUser & { assignUser?: Dispatch<PlatformUser> }

const initialPlatformUser: PlatformUser = { authenticated: false }
export const PlatformUserContext = React.createContext<UserContext>(initialPlatformUser)

export const UserProvider: React.FC<any> = ({ children, ...props }) => {
    const loc = useLocation()

    const appState = useService(AppState)
    const appService = useService(AppService)

    const [userContext, setUserContext] = React.useState<PlatformUser>({ ...initialPlatformUser })
    const [initialUserContextLoaded, setInitialUserContextLoaded] = React.useState<boolean>(false)
    const [loadError, setLoadError] = React.useState<Error | null>(null)
    const inAuth0Flow = React.useRef(false)

    const handleLoadError = React.useCallback((error: Error) => {
        setLoadError(error)

        appState.isAuthenticated = false
        appService.unsetAppLoading()

        setInitialUserContextLoaded(true)
    }, [])

    const authorizeAndLoadUserViaAuth0Code = React.useCallback(async (code: string, state: string) => {
        window.sessionStorage.removeItem(AUTH0_STATE_SESSION_KEY)

        const query = [
            `code=${code}`,
            `redirect_uri=${aqe.defaults.internalPlatformDomain}`,
            `tenant_id=${AUTH0_TENANT_ID}`,
            `client_id=${AUTH0_CLIENT_ID}`,
        ].join('&')

        try {
            const res = await axios.get<any>(`${aqe.defaults.publicApiDomain}/auth/token?${query}`, {
                bypassGlobalErrorHandlers: true,
            } as any)

            if (res.status === 200) {
                await setupInitialUserContext(UserDto.fromApiResponse(res.data.data))

                const redirect = localStorage.getItem('refreshRedirectURI')
                localStorage.removeItem('refreshRedirectURI')

                appService.route(redirect ?? ORGANIZATIONS_ROUTE)
            }
        } catch (error) {
            handleLoadError(error)
        } finally {
            inAuth0Flow.current = false
        }
    }, [])

    const loadUserViaSession = React.useCallback(async (skipValidation: boolean = false) => {
        let user: UserDto
        try {
            const validatedUser = await validateSession()
            user = UserDto.fromApiResponse(validatedUser)

            await setupInitialUserContext(user!)
        } catch (error) {
            const wrappedError = new SessionValidationFailedException(error)
            handleLoadError(wrappedError)
        }
    }, [])

    const setupInitialUserContext = React.useCallback(async (user: UserDto) => {
        await refreshUserContext(user)

        appState.isAuthenticated = true
        appService.unsetAppLoading()

        setInitialUserContextLoaded(true)
    }, [])

    /**
     * Gets all user data and loads abilities
     * Can be called at anytime to refresh user context
     */
    const refreshUserContext = React.useCallback(async (user: UserDto) => {
        try {
            await appState.fetchUserDependencies()

            appState.currentUser = user
            await appState.abilityStore.loadAbilitiesForCurrentUser()

            setUserContext({
                authenticated: true,
                user,
            })

            // TODO: migrate appState user auto refresh here
        } catch (error) {
            setLoadError(error)
        }
    }, [])

    React.useEffect(() => {
        if (!inAuth0Flow.current && loc.pathname === AUTH0_PLATFORM_REDIRECT_PATHNAME) {
            inAuth0Flow.current = true

            const params = parseQueryStringParams()
            const code = params.get('code')?.trim()
            const state = params.get('state')?.trim()
            const stateSessionValue = window.sessionStorage.getItem(AUTH0_STATE_SESSION_KEY)

            if (code && state === stateSessionValue) {
                authorizeAndLoadUserViaAuth0Code(code, state)
            } else {
                setLoadError(new Auth0InvalidAuthorizationParamsException())
            }
        }
    }, [inAuth0Flow.current, loc.pathname])

    React.useEffect(() => {
        // prevent unnecessary revalidate user, until code has been traded for auth access token - causing early
        // unset loading states / behaviors
        if (!inAuth0Flow.current) {
            loadUserViaSession()
        }
    }, [inAuth0Flow.current])

    if (loadError instanceof Auth0InvalidAuthorizationParamsException) {
        return <ErrorPage errorCode={404} showAction={true} hideLogo={false} />
    } else if (loadError && !(loadError instanceof SessionValidationFailedException)) {
        return <ErrorPage errorCode={403} showAction={true} hideLogo={false} />
    } else if (!initialUserContextLoaded) {
        return <LoadingScreen loading={true} />
    }

    return (
        <PlatformUserContext.Provider value={{ ...userContext, assignUser: setUserContext }}>
            {children}
        </PlatformUserContext.Provider>
    )
}
